import { create } from "zustand";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastAction {
  label: string;
  primary?: boolean;
  onClick?: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  dismissable: boolean;
  durationMs: number;
  exiting?: boolean;
  actions?: ToastAction[];
}

export interface ValidationModal {
  onIgnore: () => void;
}

interface UiState {
  toasts: Toast[];
  isSubmitting: boolean;
  /** Shared ID for the active validation toast — prevents duplicates across Header and BottomNav. */
  validationToastId: string | null;
  /** Incremented each time a validation toast fires — drives touchAll on inline field rows. */
  validationTouchCounter: number;
  /** True once the user has scrolled to the bottom of the welcome page — gates the Begin button. */
  welcomeScrollComplete: boolean;
  /** Dev-only override. When false, Continue/nav-with-invalid-fields skips the "fields require attention" toast and acts as if Ignore was clicked (auto-advance). Default true = production behavior. */
  showValidationToast: boolean;
  validationModal: ValidationModal | null;
  showValidationModal: (onIgnore: () => void) => void;
  dismissValidationModal: () => void;
  addToast: (message: string, type?: ToastType, durationMs?: number, actions?: ToastAction[], dismissable?: boolean) => string;
  dismissToast: (id: string) => void;
  removeToast: (id: string) => void;
  setSubmitting: (submitting: boolean) => void;
  setWelcomeScrollComplete: (complete: boolean) => void;
  setShowValidationToast: (value: boolean) => void;
}

let toastCounter = 0;

export const useUiStore = create<UiState>()((set) => ({
  toasts: [],
  isSubmitting: false,
  validationToastId: null,
  validationTouchCounter: 0,
  welcomeScrollComplete: false,
  showValidationToast: true,
  validationModal: null,
  showValidationModal: (onIgnore) => set({ validationModal: { onIgnore } }),
  dismissValidationModal: () => set({ validationModal: null }),

  addToast: (message, type = "info", durationMs = 4000, actions, dismissable = true) => {
    const id = `toast-${++toastCounter}`;
    set((state) => ({
      toasts: [...state.toasts, { id, message, type, dismissable, durationMs, actions }],
    }));
    if (durationMs > 0) {
      setTimeout(() => {
        // Trigger exit animation, then remove after animation completes
        set((state) => ({
          toasts: state.toasts.map((t) => t.id === id ? { ...t, exiting: true } : t),
        }));
        setTimeout(() => {
          set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
        }, 200);
      }, durationMs);
    }
    return id;
  },

  dismissToast: (id) => {
    set((state) => ({
      toasts: state.toasts.map((t) => t.id === id ? { ...t, exiting: true } : t),
    }));
    setTimeout(() => {
      set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
    }, 200);
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  setSubmitting: (isSubmitting) => set({ isSubmitting }),
  setWelcomeScrollComplete: (complete) => set({ welcomeScrollComplete: complete }),
  setShowValidationToast: (value) => set({ showValidationToast: value }),
}));
